import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';

import {
  getApiV5FilesIdNamingSuggestions,
  Role,
  UploadType,
  useGetApiV5FirmsFilesDocumentId,
  usePutApiV5FilesIdRename,
  v3,
} from '@liscio/api';
import {
  FileNamingPatternModal,
  useFileIntelligenceEnabled,
  useInitializedFlags,
  useTrackEvent,
} from '@liscio/common';
import {
  Alert,
  CircularProgress,
  FileIntelligenceDialog,
  FileIntelligenceUploadPicker,
  Stack,
  Typography,
  type FileIntelligenceDialogState,
} from '@liscio/ui';

import { WHAT_TO_DO_TODAY_SOURCE } from './constants';
import {
  AccountContactSelector,
  type SelectedContact,
} from 'components/AccountContactSelector/AccountContactSelector';
import {
  buildPreselectedParticipants,
  type PreselectedRecipient,
} from 'components/DesktopFileUpload/recipientAdapter';
import apiClient from 'fetch-utils/api-client';
import {
  GUIDED_DASHBOARD_RETURN_PATH,
  guidedEventProperties,
  guidedFilesPath,
  guidedMessagePath,
  type GuidedFileResumeState,
  saveGuidedFileResume,
  useGuidedFileJourney,
} from 'modules/dashboard/guidedFileIntelligence';
import {
  AI_UPLOAD_ERROR,
  FILE_UPLOAD_ACCEPT,
  isAiUploadAllowed,
} from 'modules/onboarding/views/FileIntelligenceView/constants';
import {
  getIngestionOutcome,
  IngestionOutcome,
  parseDocDetails,
} from 'modules/onboarding/views/FileIntelligenceView/FileIntelligenceView';

const GUIDED_SAMPLE_DOCUMENTS = [
  {
    id: 'w2',
    title: 'scan_2024-1.pdf',
    detectedType: 'W-2',
    filename: 'scan_2024-1.pdf',
    publicPath: '/samples/scan_2024-1.pdf',
  },
  {
    id: '1099-int',
    title: 'statement_final.pdf',
    detectedType: '1099-INT',
    filename: 'statement_final.pdf',
    publicPath: '/samples/statement_final.pdf',
  },
  {
    id: '1040',
    title: 'client_copy_final2.pdf',
    detectedType: '1040 Tax Return',
    filename: 'client_copy_final2.pdf',
    publicPath: '/samples/client_copy_final2.pdf',
  },
] as const;

const MAX_PROCESSING_POLL_ATTEMPTS = 30;

const FILE_INTELLIGENCE_DISABLED_MESSAGE =
  'File Intelligence is turned off for your firm. A firm admin can turn it on in Admin > Preferences > Settings.';
const FILE_INTELLIGENCE_UNKNOWN_MESSAGE =
  "We couldn't confirm whether File Intelligence is turned on for your firm. Close this window and try again.";

const reportFileIntelligenceSettingError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: {
      component: 'UploadFileModal',
      operation: 'fetchFileIntelligenceSetting',
    },
  });
};

class UploadValidationError extends Error {}

export interface UploadFileModalProps {
  open: boolean;
  onClose: () => void;
  defaultRecipient?: PreselectedRecipient;
  resumeResult?: GuidedFileResumeState;
}

export const UploadFileModal = ({
  open,
  onClose,
  defaultRecipient,
  resumeResult,
}: UploadFileModalProps) => {
  const navigate = useNavigate();
  const { isInitialized: flagsInitialized } = useInitializedFlags();
  const { trackEvent } = useTrackEvent();
  const recordJourney = useGuidedFileJourney();
  const renameFile = usePutApiV5FilesIdRename();
  // The backend skips ingestion for firms that turned File Intelligence off,
  // so a guided upload would never finish processing. The setting is re-read
  // each time the dialog opens, and nothing is uploaded until that read
  // confirms it is enabled.
  const fileIntelligence = useFileIntelligenceEnabled({
    enabled: open,
    onError: reportFileIntelligenceSettingError,
  });
  const fileIntelligenceEnabled = fileIntelligence.isFileIntelligenceEnabled;
  const sessionRef = useRef(1);
  const session = sessionRef.current;
  const processedDocumentRef = useRef<number | null>(null);
  const processingPollAttemptsRef = useRef(0);
  const selectionInitializedRef = useRef(false);
  const explicitSelectionRef = useRef(false);
  const uploadInFlightRef = useRef(false);
  const resumedDocumentRef = useRef<number | null>(null);
  const [state, setState] = useState<FileIntelligenceDialogState>('upload');
  const [uploadedDocId, setUploadedDocId] = useState<number | null>(null);
  const [activeSampleId, setActiveSampleId] = useState<string | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [renamedResult, setRenamedResult] = useState<string | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>(
    []
  );
  const [successfulSelection, setSuccessfulSelection] = useState<
    SelectedContact[]
  >([]);

  const sampleSelection = useMemo<SelectedContact[]>(
    () =>
      defaultRecipient
        ? buildPreselectedParticipants(defaultRecipient).map(
            ({ recipient }) => recipient
          )
        : [],
    [defaultRecipient]
  );
  const selectionValid =
    selectedContacts.length === 1 &&
    selectedContacts.every(
      (contact) =>
        contact.type === 'contact' &&
        contact.account_id != null &&
        contact.account_id === selectedContacts[0]?.account_id
    );

  const { data: processingData } = v3.useGetApiV3GetDocumentDetailsId(
    uploadedDocId?.toString() ?? '',
    {},
    {
      query: {
        enabled: open && uploadedDocId != null,
        refetchInterval: (data: unknown) => {
          const { status } = parseDocDetails(data);
          if (status === 'completed' || status === 'failed') return false;
          processingPollAttemptsRef.current += 1;
          if (
            processingPollAttemptsRef.current >= MAX_PROCESSING_POLL_ATTEMPTS
          ) {
            setState('failed');
            return false;
          }
          return 2000;
        },
      },
    }
  );
  const outcome = useMemo(() => {
    const { status, suggestedName } = parseDocDetails(processingData);
    return getIngestionOutcome(status, suggestedName);
  }, [processingData]);
  const parsedProcessing = useMemo(
    () => parseDocDetails(processingData),
    [processingData]
  );
  const processingPreviewValues = (
    processingData as
      | { data?: { data?: { preview_values?: Record<string, string> } } }
      | undefined
  )?.data?.data?.preview_values;
  const { data: detailData } = useGetApiV5FirmsFilesDocumentId(
    uploadedDocId ?? 0,
    undefined,
    {
      query: {
        enabled:
          open &&
          uploadedDocId != null &&
          (outcome === IngestionOutcome.named || state === 'result'),
      },
    }
  );
  const file = detailData?.data;
  const documentUuid =
    file?.uuid || parsedProcessing.documentUuid || resumeResult?.document.uuid;

  useEffect(() => {
    if (
      !open ||
      !resumeResult ||
      resumedDocumentRef.current === resumeResult.document.id
    )
      return;
    const resumedSelection: SelectedContact[] = [
      {
        id: resumeResult.selection.contactId,
        uuid: resumeResult.selection.contactUuid,
        cpa_user_id: resumeResult.selection.cpaUserId,
        first_name: resumeResult.selection.firstName,
        last_name: resumeResult.selection.lastName,
        email: '',
        type: 'contact',
        account_id: resumeResult.selection.accountId,
        account_name: resumeResult.selection.accountName,
      },
    ];
    resumedDocumentRef.current = resumeResult.document.id;
    selectionInitializedRef.current = true;
    explicitSelectionRef.current = true;
    processedDocumentRef.current = resumeResult.document.id;
    setSelectedContacts(resumedSelection);
    setSuccessfulSelection(resumedSelection);
    setUploadedDocId(resumeResult.document.id);
    setRenamedResult(
      resumeResult.document.suggestedName || resumeResult.document.displayName
    );
    setState('result');
  }, [open, resumeResult]);

  const resetFile = useCallback(() => {
    setState('upload');
    setUploadedDocId(null);
    setActiveSampleId(null);
    setTemplateOpen(false);
    setUploadError(null);
    setRenamedResult(null);
    setSuccessfulSelection([]);
    processedDocumentRef.current = null;
    processingPollAttemptsRef.current = 0;
  }, []);

  const resetAll = useCallback(() => {
    resetFile();
    setSelectedContacts([]);
    selectionInitializedRef.current = false;
    explicitSelectionRef.current = false;
  }, [resetFile]);

  useEffect(() => {
    if (
      !open ||
      sampleSelection.length === 0 ||
      selectionInitializedRef.current ||
      explicitSelectionRef.current
    )
      return;
    setSelectedContacts(sampleSelection);
    selectionInitializedRef.current = true;
  }, [open, sampleSelection]);

  useEffect(() => {
    if (!open) {
      sessionRef.current += 1;
      resetAll();
    }
  }, [open, resetAll]);

  const handleClose = useCallback(() => {
    sessionRef.current += 1;
    resetAll();
    onClose();
  }, [onClose, resetAll]);

  const acceptUploadedDocument = useCallback(
    (docId: number, uploadSession: number, selection: SelectedContact[]) => {
      if (!open || uploadSession !== sessionRef.current) return;
      setActiveSampleId(null);
      processingPollAttemptsRef.current = 0;
      setUploadedDocId(docId);
      setSuccessfulSelection(selection);
      setState('processing');
      recordJourney('file_uploaded', docId);
    },
    [open, recordJourney]
  );

  const uploadToSelection = useCallback(
    async (file: File) => {
      if (!fileIntelligenceEnabled) {
        throw new UploadValidationError(FILE_INTELLIGENCE_DISABLED_MESSAGE);
      }
      if (!selectionValid) {
        throw new UploadValidationError('Select an Account and one Contact.');
      }
      if (uploadInFlightRef.current) {
        throw new UploadValidationError('An upload is already in progress.');
      }

      const frozenSelection = selectedContacts.map((contact) => ({
        ...contact,
      }));
      const accountId = frozenSelection[0]?.account_id;
      const contactIds = frozenSelection.map((contact) =>
        Number(contact.contact_id ?? contact.id)
      );
      if (
        !accountId ||
        contactIds.some((id) => !Number.isInteger(id) || id <= 0)
      ) {
        throw new UploadValidationError(
          'The selected filing destination is unavailable.'
        );
      }

      uploadInFlightRef.current = true;
      try {
        const uploaded = await apiClient.documents.uploadFile({
          file,
          type: UploadType.NewFile,
          is_draft: false,
          is_private_to_firm: false,
          account_id: accountId,
          contact_id: contactIds[0],
        });
        const uploadBody = uploaded.data as typeof uploaded.data & {
          message?: string;
          status?: number | string;
        };
        if (
          uploadBody.status === 'unprocessable_entity' ||
          uploadBody.status === 422 ||
          !uploadBody.data?.id
        ) {
          throw new UploadValidationError(
            uploadBody.message || 'The file could not be uploaded.'
          );
        }

        const docId = uploadBody.data.id;
        const association =
          await apiClient.documents.associateUploadedDocuments({
            doc_ids: [docId],
            recipients: contactIds.map(
              (contactId): [number, number, string] => [
                contactId,
                accountId,
                'contact',
              ]
            ),
            send_message: false,
            is_private_to_firm: true,
          });
        if (
          !association ||
          association.success === false ||
          association.status === 'unprocessable_entity' ||
          association.status === 422 ||
          (association.status === 200 && association.success !== true)
        ) {
          throw new UploadValidationError(
            association?.message ||
              'The file could not be associated with the selected client.'
          );
        }

        return { docId, frozenSelection };
      } finally {
        uploadInFlightRef.current = false;
      }
    },
    [fileIntelligenceEnabled, selectedContacts, selectionValid]
  );

  const handleSample = useCallback(
    async (sampleId: string) => {
      const sample = GUIDED_SAMPLE_DOCUMENTS.find(
        (candidate) => candidate.id === sampleId
      );
      if (!sample) return;
      const uploadSession = sessionRef.current;
      setActiveSampleId(sampleId);
      setUploadError(null);
      setState('uploading');
      try {
        const response = await fetch(
          `${window.location.origin}${sample.publicPath}`
        );
        if (!response.ok) throw new Error('Sample download failed');
        const fileBlob = await response.blob();
        const sampleFile = new File([fileBlob], sample.filename, {
          type: fileBlob.type || 'application/pdf',
        });
        if (!isAiUploadAllowed(sampleFile))
          throw new Error('Unsupported sample');
        const { docId, frozenSelection } = await uploadToSelection(sampleFile);
        if (uploadSession !== sessionRef.current) return;
        trackEvent('file_uploaded', guidedEventProperties(docId));
        acceptUploadedDocument(docId, uploadSession, frozenSelection);
      } catch (error) {
        if (uploadSession === sessionRef.current) {
          setActiveSampleId(null);
          setUploadError(
            error instanceof UploadValidationError
              ? error.message
              : 'Could not upload the sample. Please try again.'
          );
          setState('upload');
        }
      }
    },
    [acceptUploadedDocument, trackEvent, uploadToSelection]
  );

  const handleFile = useCallback(
    async (selectedFile: File) => {
      if (!isAiUploadAllowed(selectedFile)) {
        setUploadError(AI_UPLOAD_ERROR);
        return;
      }

      const uploadSession = sessionRef.current;
      setUploadError(null);
      setState('uploading');
      try {
        const { docId, frozenSelection } = await uploadToSelection(
          selectedFile
        );
        if (uploadSession !== sessionRef.current) return;
        trackEvent('file_uploaded', guidedEventProperties(docId));
        acceptUploadedDocument(docId, uploadSession, frozenSelection);
      } catch (error) {
        if (uploadSession !== sessionRef.current) return;
        setUploadError(
          error instanceof UploadValidationError
            ? error.message
            : 'Could not upload the file. Please try again.'
        );
        setState('upload');
      }
    },
    [acceptUploadedDocument, trackEvent, uploadToSelection]
  );

  useEffect(() => {
    if (!open || uploadedDocId == null) return;
    if (outcome === IngestionOutcome.noName) setState('no-name');
    if (outcome === IngestionOutcome.failed) setState('failed');
    if (
      outcome === IngestionOutcome.named &&
      processedDocumentRef.current !== uploadedDocId
    ) {
      processedDocumentRef.current = uploadedDocId;
      trackEvent(
        'ai_ingestion_completed',
        guidedEventProperties(uploadedDocId)
      );
      recordJourney('file_processed', uploadedDocId);
      setState('result');
    }
  }, [open, outcome, recordJourney, trackEvent, uploadedDocId]);

  const result = useMemo(
    () =>
      uploadedDocId
        ? {
            originalName:
              file?.original_name ||
              file?.name ||
              resumeResult?.document.originalName ||
              parsedProcessing.originalName ||
              `Document ${uploadedDocId}`,
            suggestedName:
              renamedResult ||
              file?.resolved_name ||
              file?.suggested_name ||
              resumeResult?.document.suggestedName ||
              resumeResult?.document.displayName ||
              parsedProcessing.suggestedName ||
              'Suggested name unavailable',
            filedLocation:
              successfulSelection[0]?.account_name ||
              file?.account?.name ||
              'Firm files',
          }
        : undefined,
    [
      file,
      parsedProcessing,
      renamedResult,
      resumeResult,
      successfulSelection,
      uploadedDocId,
    ]
  );

  const handleTemplateSaved = useCallback(async () => {
    if (!documentUuid) {
      return 'The template was saved, but this file was not renamed. Please try again.';
    }
    try {
      const suggestion = await getApiV5FilesIdNamingSuggestions(documentUuid);
      if (!suggestion.suggestion)
        throw new Error('No updated name was generated');
      await renameFile.mutateAsync({
        id: documentUuid,
        data: {
          name: suggestion.suggestion,
          ai_file_name_suggestion_uuid: suggestion.ai_file_name_suggestion_uuid,
        },
      });
      setRenamedResult(suggestion.suggestion);
      setTemplateOpen(false);
    } catch {
      return 'The template was saved, but this file was not renamed. Please try again.';
    }
  }, [documentUuid, renameFile]);

  const messageSelection = successfulSelection[0];
  const canSendMessage =
    flagsInitialized &&
    uploadedDocId != null &&
    Number(messageSelection?.cpa_user_id) > 0 &&
    Number(messageSelection?.account_id) > 0;

  const preserveResultForReturn = useCallback(() => {
    if (uploadedDocId == null || !messageSelection) return;
    const accountId = Number(messageSelection.account_id);
    const contactId = Number(
      messageSelection.contact_id ?? messageSelection.id
    );
    const cpaUserId = Number(messageSelection.cpa_user_id);
    if (
      !Number.isInteger(accountId) ||
      accountId <= 0 ||
      !Number.isInteger(contactId) ||
      contactId <= 0
    )
      return;
    saveGuidedFileResume({
      document: {
        id: uploadedDocId,
        uuid: documentUuid,
        originalName:
          file?.original_name || result?.originalName || 'Uploaded file',
        suggestedName:
          file?.suggested_name ||
          result?.suggestedName ||
          'Suggested file name',
        displayName:
          file?.resolved_name ||
          result?.suggestedName ||
          result?.originalName ||
          '',
      },
      selection: {
        contactId,
        contactUuid: messageSelection.uuid,
        cpaUserId:
          Number.isInteger(cpaUserId) && cpaUserId > 0 ? cpaUserId : undefined,
        firstName: messageSelection.first_name,
        lastName: messageSelection.last_name,
        accountId,
        accountName: messageSelection.account_name || '',
      },
    });
  }, [documentUuid, file, messageSelection, result, uploadedDocId]);

  const openFolder = useCallback(() => {
    if (uploadedDocId == null) return;
    preserveResultForReturn();
    navigate(guidedFilesPath(uploadedDocId));
  }, [
    navigate,
    preserveResultForReturn,
    uploadedDocId,
  ]);

  const openMessage = useCallback(() => {
    if (!canSendMessage || uploadedDocId == null || !messageSelection) return;

    const cpaUserId = Number(messageSelection.cpa_user_id);
    const accountId = Number(messageSelection.account_id);
    const contactId = Number(
      messageSelection.contact_id ?? messageSelection.id
    );
    preserveResultForReturn();
    trackEvent(
      'guided_file_intelligence_action_selected',
      guidedEventProperties(uploadedDocId, { action: 'send_message' })
    );
    recordJourney('file_send_selected', uploadedDocId);
    navigate(guidedMessagePath(uploadedDocId, accountId, contactId), {
      state: {
        guidedDocument: {
          id: uploadedDocId,
          uuid: documentUuid,
          name:
            file?.name || parsedProcessing.originalName || result?.originalName,
          doc_name:
            file?.name || parsedProcessing.originalName || result?.originalName,
          original_name: file?.original_name || result?.originalName,
          suggested_name: file?.suggested_name || result?.suggestedName,
          display_name: file?.resolved_name || result?.suggestedName,
          is_active: true,
          is_private_to_firm: false,
        },
        form: {
          participants: [
            {
              id: cpaUserId,
              cpa_user_id: cpaUserId,
              contact_id: contactId,
              contact_name: [
                messageSelection.first_name,
                messageSelection.last_name,
              ]
                .filter(Boolean)
                .join(' '),
              type: Role.User,
              account_id: accountId,
              account_name: messageSelection.account_name,
              is_recipient: true,
            },
          ],
        },
        goBackUrl: GUIDED_DASHBOARD_RETURN_PATH,
      },
    });
  }, [
    canSendMessage,
    documentUuid,
    file,
    messageSelection,
    navigate,
    parsedProcessing.originalName,
    preserveResultForReturn,
    recordJourney,
    result,
    trackEvent,
    uploadedDocId,
  ]);

  const fileIntelligenceGate = fileIntelligence.isLoading ? (
    <Stack
      role="status"
      direction="row"
      sx={{ alignItems: 'center', gap: 1.5, mb: 2 }}
    >
      <CircularProgress size={20} />
      <Typography variant="body2" color="text.secondary">
        Checking File Intelligence settings...
      </Typography>
    </Stack>
  ) : (
    <Alert
      severity={fileIntelligence.isUnknown ? 'error' : 'info'}
      sx={{ mb: 2 }}
    >
      {fileIntelligence.isUnknown
        ? FILE_INTELLIGENCE_UNKNOWN_MESSAGE
        : FILE_INTELLIGENCE_DISABLED_MESSAGE}
    </Alert>
  );

  const uploadContent = !fileIntelligenceEnabled ? (
    fileIntelligenceGate
  ) : (
    <>
      <AccountContactSelector
        value={selectedContacts}
        onInteract={() => {
          explicitSelectionRef.current = true;
        }}
        onChange={(selection) => {
          explicitSelectionRef.current = true;
          setSelectedContacts(selection);
        }}
        label="File to"
        placeholder="Search Accounts and Contacts"
        disabled={state !== 'upload'}
        selectionMode="single"
        multiAccount={false}
        allowCreateContact={false}
        allowCreateAccount={false}
        includeEmployees={false}
        accountRequired
        contactRequired
        showContactCard={false}
        showContactCardNavigationLinks={false}
      />
      <FileIntelligenceUploadPicker
        key={session}
        accept={FILE_UPLOAD_ACCEPT}
        disabled={!selectionValid || state !== 'upload'}
        onFileSelected={(file) => void handleFile(file)}
      />
    </>
  );

  return (
    <>
      <FileIntelligenceDialog
        open={open && !templateOpen}
        state={state}
        uploadContent={uploadContent}
        samples={fileIntelligenceEnabled ? GUIDED_SAMPLE_DOCUMENTS : []}
        activeSampleId={activeSampleId}
        samplesDisabled={!selectionValid || !fileIntelligenceEnabled}
        uploadError={uploadError}
        result={result}
        onClose={handleClose}
        onSample={(id) => void handleSample(id)}
        onTryAnother={resetFile}
        onOpenFolder={openFolder}
        onOpenTemplate={() => setTemplateOpen(true)}
        onSend={canSendMessage ? openMessage : undefined}
      />
      <FileNamingPatternModal
        open={open && templateOpen}
        onClose={() => setTemplateOpen(false)}
        originalName={result?.originalName}
        suggestedName={result?.suggestedName}
        documentUuid={documentUuid}
        previewValues={processingPreviewValues}
        source={WHAT_TO_DO_TODAY_SOURCE}
        trackEvent={trackEvent}
        onTemplateSaved={handleTemplateSaved}
      />
    </>
  );
};
